import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { Phone, ArrowUp, ArrowDown, Pen, ChevronDown, X, AlertTriangle, ShieldCheck, Flag as FlagIcon } from "lucide-react";
import SharedDataContext from "../../../SharedDataContext";
import { useTableData } from "../../../hooks/useTableData";
import {
  getAllPhoneNumbers,
  getAccessiblePhoneNumbers,
  updatePhoneNumber,
} from "../../../apis/phoneInventory";
import TableLoader from "../../Loaders/TableLoader";
import NoData from "../../NoData";
import Pagination from "../../Pagination";
import EditColumn from "../../EditColumn";
import Filter from "../../Filter";
import { returnArray, isEmptyObject } from "../../../utils/GenericFn";
import { Switch } from "../../ui/switch";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { usePhoneInventoryStore } from "../../../stores/usePhoneInventoryStore";
import { Button } from "../../ui/button";
import { Plus } from "lucide-react";

import PurchaseNumberPanel from "./PurchaseNumberPanel";
import ReplaceNumberModal from "./ReplaceNumberModal";
import FeaturePromotionView from "./FeaturePromotionView";
import PhoneInventoryEmptyState from "./PhoneInventoryEmptyState";
import PageHeader from "../../PageHeader";
import { Badge } from "../../ui/badge";
import Flag from "../../Flag";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import { useCountries } from "../../../hooks/useCountries";
import Input from "../../Tags/Input";

export default function AllNumbers() {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const { workspaceSlug } = useParams();
  const navigate = useNavigate();
  const isAdmin = activeWorkspace?.role === "Owner" || activeWorkspace?.role === "Admin";
  const { openPurchaseNumberPanel } = usePhoneInventoryStore();
  const captureEvent = useCaptureEvent();

  const queryClient = useQueryClient();

  const [selectedRows, setSelectedRows] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [updatingNumbers, setUpdatingNumbers] = useState(new Set());
  const [replaceModalNumber, setReplaceModalNumber] = useState(null);
  const queryKeyName = isAdmin ? "get_phone_numbers_admin" : "get_phone_numbers_user";
  const { countries } = useCountries();
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const countryDropdownRef = useRef(null);
  const [countryFilterValue, setCountryFilterValue] = useState("");

  const handleOpenPurchasePanel = () => {
    captureEvent("SET_PhoneInventory_PurchasePanelOpen");
    openPurchaseNumberPanel("inventory", {
      returnTo: "inventory",
      workspaceSlug: workspaceSlug,
      sourceComponent: "phone-inventory",
    });
  };

  useEffect(() => {
    const handleInventoryUpdate = (event) => {
      const { number } = event.detail;

      queryClient.invalidateQueries(isAdmin ? "get_phone_numbers_admin" : "get_phone_numbers_user");
      toast.success(`Number ${number.phone_number} added to inventory!`);
    };

    const handleUserSelection = (event) => {
      const { number, user } = event.detail;
      toast.success(`Number ${number.phone_number} selected!`);
    };

    window.addEventListener("numberAddedToInventory", handleInventoryUpdate);
    window.addEventListener("numberSelectedForUser", handleUserSelection);

    return () => {
      window.removeEventListener("numberAddedToInventory", handleInventoryUpdate);
      window.removeEventListener("numberSelectedForUser", handleUserSelection);
    };
  }, [queryClient, isAdmin]);

  const {
    data,
    paginationInfo,
    filterMetadata,
    columnMetadata,
    apiResponse,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    filters,
    sort,
    sortedVisibleColumns,
    getPageNumber,
    getFiltersObject,
    handleSetSort,
    getColumns,
    query,
  } = useTableData({
    queryKey: queryKeyName,
    fetchFn: isAdmin ? getAllPhoneNumbers : getAccessiblePhoneNumbers,
    defaultHeaders: { "X-Workspace-Id": activeWorkspace?.id },
    enabled: !!activeWorkspace?.id,
    onSuccess: (data) => {
      setIsInitialLoad(false);
    },
    onError: (error) => {
      console.error("Phone Numbers API error:", error);
      setIsInitialLoad(false);
    },
  });

  const spamSummary = apiResponse?.spamSummary || {};

  const updatePhoneNumberMutation = useMutation(updatePhoneNumber, {
    onSuccess: (data, variables) => {
      setUpdatingNumbers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.uniqueRowId);
        return newSet;
      });

      // Show success toast
      toast.success(
        `Recording ${variables.data.recording_enabled ? "enabled" : "disabled"} for phone number`
      );

      // Optional: Invalidate cache after a delay to allow backend to process
      // This prevents race conditions with optimistic updates
      queryClient.invalidateQueries(queryKeyName);
      captureEvent("SET_PhoneInventory_RecordingToggleSuccess", {
        phone_number_id: variables.uniqueRowId,
        enabled: variables.data.recording_enabled,
      });
    },
    onError: (error, variables) => {
      console.error("Error updating phone number:", error);

      setUpdatingNumbers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.uniqueRowId);
        return newSet;
      });

      // Show error toast
      toast.error("Failed to update recording setting. Please try again.");
      captureEvent("SET_PhoneInventory_RecordingToggleFailure", {
        phone_number_id: variables.uniqueRowId,
        enabled: variables.data.recording_enabled,
        error: error?.message || "unknown_error",
      });
    },
  });

  const mappedNumbers = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((item, index) => {
      const originalId = item.id;
      const uniqueId = `${originalId}-${item.phone_number}-${item.type || "unknown"}-${item.tag || "notag"}-${index}`;

      return {
        ...item,
        originalId: originalId, // Store original ID for API calls
        id: uniqueId, // Use unique ID for React rendering and state management
        // Transform assigned users for display
        assigned_users_display: Array.isArray(item.assigned_users) ? item.assigned_users : [],
        assigned_users_names: Array.isArray(item.assigned_users)
          ? item.assigned_users.map((user) => user.name).join(", ")
          : item.assigned_users === "null"
            ? "None"
            : "None",
        // Transform capabilities for display
        capabilities_list: item.capabilities
          ? Object.entries(item.capabilities)
              .filter(([key, value]) => value)
              .map(([key]) => key.toUpperCase())
              .join(", ")
          : "None",
        // Keep raw capabilities for badge rendering
        capabilities: item.capabilities || {},
        // Format location
        location_display: [item.locality, item.region, item.country].filter(Boolean).join(", "),
        // Status based on tag or type
        status_display:
          item.tag && item.tag !== "null" ? item.tag : item.type === "Assigned" ? "Active" : "Available",
      };
    });
  }, [data]);

  useEffect(() => {
    const value = filters?.country?.value;
    if (!value) {
      setCountryFilterValue("");
      return;
    }
    setCountryFilterValue(value.split(",")[0]);
  }, [filters?.country?.value]);

  const selectedCountry = useMemo(() => {
    if (!countryFilterValue || !Array.isArray(countries)) return null;
    return countries.find((country) => country.code === countryFilterValue) || null;
  }, [countryFilterValue, countries]);

  const filteredCountries = useMemo(() => {
    if (!Array.isArray(countries)) return [];
    const term = countrySearchTerm.toLowerCase();
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(term) || country.code.toLowerCase().includes(term)
    );
  }, [countries, countrySearchTerm]);

  const applyFilters = (data = {}, countryCodeOverride = null) => {
    const merged = { ...data };
    const countryCode = countryCodeOverride !== null ? countryCodeOverride : countryFilterValue;

    if (countryCode) {
      merged.country = { selectedScope: "country", value: countryCode, type: "select" };
    } else {
      delete merged.country;
    }
    getFiltersObject(merged);
  };

  const handleCountrySelect = (code) => {
    setCountryFilterValue(code);
    const newFilters = { ...(filters || {}) };
    if (code) {
      newFilters.country = { selectedScope: "country", value: code, type: "select" };
    } else {
      delete newFilters.country;
    }
    applyFilters(newFilters, code);
    setIsCountryDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setIsCountryDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle workspace changes
  useEffect(() => {
    setIsInitialLoad(true);
  }, [workspaceSlug]);

  useEffect(() => {
    if (activeWorkspace?.id) {
      captureEvent("SET_PhoneInventory_PageView", {
        workspace_id: activeWorkspace.id,
        role: activeWorkspace?.role,
      });
    }
  }, [activeWorkspace?.id, activeWorkspace?.role, captureEvent]);

  const handleSelectAll = () => {
    setSelectedRows(selectedRows.length === mappedNumbers?.length ? [] : mappedNumbers.map((item) => item.id));
  };

  const handleSelectRow = (isChecked, id) => {
    setSelectedRows((prev) => (isChecked ? [...prev, id] : prev.filter((rowId) => rowId !== id)));
  };

  const renderAssignedUsers = (assignedUsers) => {
    const users = Array.isArray(assignedUsers) ? assignedUsers : [];
    if (users.length === 0) return <span className="text-gray-400">None</span>;

    const visibleCount = users.length > 2 ? 2 : users.length;
    const visibleUsers = users.slice(0, visibleCount);
    const hiddenUsers = users.slice(visibleCount);

    return (
      <div className="flex items-center gap-1">
        {/* <Users className="h-4 w-4 text-gray-500" /> */}
        <div className="flex flex-wrap gap-1 items-center">
          {visibleUsers.map((user, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
            >
              {user.name}
            </span>
          ))}
          {hiddenUsers.length > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
              +{hiddenUsers.length} more
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderStatus = (item) => {
    const status = item.status_display;
    let className = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ";

    switch (status?.toLowerCase()) {
      case "active":
        className += "bg-green-100 text-green-800";
        break;
      case "validation pending":
        className += "bg-yellow-100 text-yellow-800";
        break;
      case "inactive":
        className += "bg-gray-100 text-gray-800";
        break;
      default:
        className += "bg-blue-100 text-blue-800";
    }

    return <span className={className}>{status}</span>;
  };

  const handleRecordingToggle = (phoneNumberId, currentStatus) => {
    setUpdatingNumbers((prev) => new Set(prev.add(phoneNumberId)));

    const item = mappedNumbers.find((item) => item.id === phoneNumberId);
    const originalApiId = item?.originalId || phoneNumberId;

    captureEvent("SET_PhoneInventory_RecordingToggleClick", {
      phone_number_id: originalApiId,
      enabled: !currentStatus,
    });
    updatePhoneNumberMutation.mutate({
      phoneNumberId: originalApiId,
      uniqueRowId: phoneNumberId,
      data: { recording_enabled: !currentStatus },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const handleRowClick = (phoneNumber) => {
    const originalId = phoneNumber.originalId || phoneNumber.id;
    navigate(`/${workspaceSlug}/numbers-inventory/${originalId}/settings`);
  };

  const renderCapabilities = (capabilities) => {
    if (!capabilities) return <span className="text-gray-400">-</span>;

    const capabilityLabels = {
      voice: "Call",
      sms: "SMS",
      mms: "MMS",
      fax: "Fax",
    };

    return (
      <div className="flex flex-wrap gap-1">
        {Object.entries(capabilities).map(([key, isActive]) => {
          const label = capabilityLabels[key] || key;
          return (
            <Badge key={key} variant={isActive ? "indigo" : "gray"}>
              {isActive && <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5 flex-shrink-0"></span>}
              {label}
            </Badge>
          );
        })}
      </div>
    );
  };

  const renderRecordingEnabled = (item) => {
    const isUpdating = updatingNumbers.has(item.id);

    return (
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Switch
          checked={item.recording_enabled}
          onCheckedChange={() => handleRecordingToggle(item.id, item.recording_enabled)}
          disabled={isUpdating || !isAdmin}
          className={`data-[state=checked]:bg-[#7F56D9] data-[state=unchecked]:bg-gray-300 ${
            isUpdating ? "opacity-50 cursor-not-allowed" : ""
          }`}
        />

        {isUpdating && (
          <div className="inline-flex items-center">
            <div className="loader loader_circle w-3 h-3"></div>
          </div>
        )}
      </div>
    );
  };

  const renderCellContent = (columnKey, item) => {
    switch (columnKey) {
      case "phone_number": {
        const isSpam = item.spam_status === "flagged";
        const isReplaced = item.spam_status === "replaced";
        return (
          <div className="flex items-center">
            <span className="text-[40px] rounded-full inline-flex items-center justify-center overflow-hidden border border-border  mr-2">
              <Flag code={item?.country} squared />
            </span>
            <div className="">
              {item.label ? (
                <div>
                  <div className={`font-semibold text-sm capitalize flex items-center gap-1 ${isSpam ? "text-red-600" : isReplaced ? "text-[#717680]" : "text-[#101828] dark:text-white"}`}>
                    {item.label}
                    {isReplaced && (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                        <path d="M2.66675 4.66667H10.0001M10.0001 4.66667L7.33342 7.33333M10.0001 4.66667L7.33341 2M2.66675 11.3333H13.3334M13.3334 11.3333L10.6667 14M13.3334 11.3333L10.6667 8.66667" stroke="#A7A7A7" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className="text-sm text-[#535862] dark:text-gray-400 font-normal mt-1">
                    {item.phone_number}<span className="text-xs text-gray-500">({item.type})</span>
                  </div>                
                </div>
              ) : (
                <div>
                  <div className={`font-semibold text-sm flex items-center gap-1 ${isSpam ? "text-red-600" : "text-[#101828] dark:text-white"}`}>
                    {item.phone_number}
                    {isSpam && <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                  </div>
                  <div className="text-sm text-[#535862] dark:text-gray-400 font-normal">({item.type})</div>
               
                </div>
              )}
            </div>
          </div>
        );
      }

      case "number_type":
        return (
          <div>
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-background text-gray-600 border border-border shadow-sm">
              {item.number_type}
            </span>
          </div>
        );

      case "status":
        return renderCapabilities(item.capabilities);

      case "assigned_users":
        if (item?.type === "Shared") {
          return <span className="text-gray-700">Across workspace</span>;
        } else {
          return renderAssignedUsers(item.assigned_users_display);
        }

      case "recording_enabled":
        return renderRecordingEnabled(item);

      case "capabilities":
        return renderCapabilities(item.capabilities);

      case "spam_status":
        if (item.spam_status === "flagged") {
          return (
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-[#FEF3F2] text-[#B42318] border border-[#FECDCA] shadow-sm">
                 <AlertTriangle className="h-3 w-3 mr-1" />
                 Spam
            </span>
          );
        } else if (item.spam_status === "clean") {
          return (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-background text-gray-600 border border-border shadow-sm">
            Clean
          </span>
          );
        } else if (item.spam_status === "replaced") {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-background text-gray-600 border border-border shadow-sm">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.25 10.75L4.75 9.25M4.75 9.25L6.25 7.75M4.75 9.25H7.25C9.183 9.25 10.75 7.683 10.75 5.75C10.75 4.3538 9.93247 3.14855 8.75 2.58682M2.75 8.91318C1.56753 8.35145 0.75 7.1462 0.75 5.75C0.75 3.817 2.317 2.25 4.25 2.25H6.75M6.75 2.25L5.25 0.75M6.75 2.25L5.25 3.75" stroke="#717680" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Rotated
            </span>
          );
        }
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-background text-gray-600 border border-border shadow-sm">
            Unknown
          </span>
        );

      default:
        return item?.[columnKey] || "-";
    }
  };

  const hasFilters = !isEmptyObject(filters);
  const hasData = Array.isArray(mappedNumbers) && mappedNumbers.length > 0;
  const baseEmpty = !isFetching && isSuccess && !isInitialLoad && !hasData;
  const shouldShowFilteredEmptyState = baseEmpty && hasFilters;
  const shouldShowEmptyState = baseEmpty && !hasFilters;

  if (query?.error?.status === 403 || query?.error?.response?.status === 403) {
    return (
      <>
        <PageHeader heading="Phone numbers" description="Add new or manage existing phone numbers" />
        <FeaturePromotionView />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        heading="Phone numbers"
        description="Add new or manage existing phone numbers"
        cta={
          <>
            <div>
              <Button
                onClick={handleOpenPurchasePanel}
                className="bg-[#7F56D9] hover:bg-purple-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add new number
              </Button>
            </div>
          </>
        }
      />

      {apiResponse && (
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-5 bg-background border border-border rounded-lg">
            <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#181D27] mb-1">{spamSummary.clean ?? 0}</p>
              <p className="text-sm text-[#181D27]">Clean numbers</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-5 bg-background border border-border rounded-lg">
            <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0">
              <FlagIcon className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#181D27] mb-1">{spamSummary.flagged ?? 0}</p>
              <p className="text-sm text-[#181D27]">Flagged numbers</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-5 bg-background border border-border rounded-lg">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.28932 0.474637C10.2204 -0.146095 11.4301 -0.158963 12.3741 0.441818L16.2884 2.93271H17.8981C18.4184 2.93271 18.8401 3.35447 18.8401 3.87472V12.3528C18.8401 12.8731 18.4184 13.2949 17.8981 13.2949H16.4665C16.5226 14.1872 16.1022 15.0944 15.2564 15.6019L10.4584 18.4807C9.85265 18.8441 9.10073 18.8356 8.50773 18.4736C7.84803 19.057 6.83254 19.1212 6.09583 18.5424L1.25315 14.7374C0.531175 14.1702 0.348603 13.201 0.721387 12.4327C0.266101 12.0757 0 11.529 0 10.9502V3.87478C0 3.35452 0.42176 2.93276 0.942017 2.93276H5.60211L9.28932 0.474637ZM2.04666 11.0767L2.69857 10.5063C3.85744 9.49221 5.61481 9.59263 6.65065 10.7321L9.19615 13.5321C10.0152 14.4332 10.1646 15.7569 9.56891 16.8171L14.2871 13.9863C14.5522 13.8273 14.6481 13.5093 14.5493 13.2415L10.3789 7.40311C10.1446 7.07515 9.72764 6.93255 9.3416 7.04836L7.00045 7.75071C6.00465 8.04942 4.92522 7.77727 4.19008 7.04217L3.91429 6.76638C3.37921 6.2313 3.24084 5.46978 3.46601 4.8168H1.88403V10.9502L2.04666 11.0767ZM11.3627 2.03131C11.048 1.83105 10.6448 1.83533 10.3344 2.04225L5.2465 5.43417L5.52229 5.70996C5.76734 5.95501 6.12715 6.04571 6.45909 5.94613L8.80022 5.24379C9.95834 4.89635 11.2092 5.32416 11.912 6.30804L15.5568 11.4108H16.9561V4.81674H16.2884C15.9301 4.81674 15.5792 4.71457 15.2769 4.5222L11.3627 2.03131ZM3.93921 11.9241L2.41715 13.2559L7.25983 17.0609L7.92292 15.9005C8.12546 15.5461 8.07666 15.1015 7.80206 14.7994L5.25658 11.9994C4.9113 11.6196 4.3255 11.5861 3.93921 11.9241Z" fill="#717680"/>
              </svg>
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#181D27] mb-1">{spamSummary.unknown ?? 0}</p>
              <p className="text-sm text-[#181D27]">Unknown numbers</p>
            </div>
          </div>
        </div>
      )}

      {(hasData || hasFilters) && (
        <div className="flex justify-between items-center">
          <div className="space-x-3 inline-flex items-center">
                    {filterMetadata && (
                      <>
                        <span className="text-sm text-gray-500">Filters:</span>
                        <div className="relative inline-block" ref={countryDropdownRef}>
                          <Button
                            variant="outline"
                            className="inline-flex items-center gap-2 border-border bg-background h-[34px]"
                            onClick={() => setIsCountryDropdownOpen((prev) => !prev)}
                          >
                    {selectedCountry ? (
                      <>
                        <Flag code={selectedCountry.code} squared />
                        <span className="text-xs font-normal">{selectedCountry.name}</span>
                        <button
                          type="button"
                          className="ml-1 inline-flex items-center justify-center rounded-full p-1 hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCountrySelect("");
                          }}
                        >
                          <X className="h-3 w-3 text-gray-500" />
                        </button>
                      </>
                    ) : (
                      <span className="text-xs font-normal">Country</span>
                    )}
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </Button>

                  {isCountryDropdownOpen && (
                    <div className="absolute z-20 mt-2 w-64 rounded-[--radius] border border-border bg-background shadow-lg p-3">
                      <Input
                        className="w-full"
                        placeholder="Search country..."
                        value={countrySearchTerm}
                        onChange={(e) => setCountrySearchTerm(e.target.value)}
                      />
                      <div className="mt-2 max-h-64 overflow-y-auto">
                        <button
                          type="button"
                          className="w-full text-left px-2 py-2 rounded hover:bg-gray-50 text-sm text-gray-700"
                          onClick={() => handleCountrySelect("")}
                        >
                          Clear selection
                        </button>
                        {filteredCountries.map((country) => (
                          <button
                            type="button"
                            key={country.code}
                            className={`w-full flex items-center px-2 py-2 rounded hover:bg-gray-50 text-sm ${
                              country.code === countryFilterValue ? "bg-gray-100" : ""
                            }`}
                            onClick={() => handleCountrySelect(country.code)}
                          >
                            <Flag code={country.code} squared />
                            <span className="ml-2">{country.name}</span>
                            <span className="ml-auto text-xs text-gray-500">{country.code}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <Filter filterMetadata={filterMetadata} getFiltersObject={applyFilters} />
              </>
            )}
          </div>
          <div className="space-x-3">
            {columnMetadata && <EditColumn columnMetadata={columnMetadata} getColumns={getColumns} />}
          </div>
        </div>
      )}

      {/* Table */}
      <div>
        {hasData && isSuccess && (
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden border border-border rounded-[--radius]">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-background">
                      <tr>
                        {returnArray(sortedVisibleColumns).map((column, index) => (
                          <th
                            key={index}
                            scope="col"
                            className={`px-3 py-3.5 text-left text-xs font-medium text-text-600 ${
                              column.sortable ? "cursor-pointer" : ""
                            }`}
                            onClick={() => (column.sortable ? handleSetSort(column.key) : void 0)}
                          >
                            <span className={"inline-flex items-center"}>
                              {column.display_name}
                              {column.sortable && (
                                <>
                                  {sort === `-${column.key}` ? (
                                    <ArrowDown className="inline-block ml-1 h-3 w-3" />
                                  ) : sort === column.key ? (
                                    <ArrowUp className="inline-block ml-1 h-3 w-3" />
                                  ) : (
                                    <ArrowDown className="inline-block ml-1 h-3 w-3 opacity-50" />
                                  )}
                                </>
                              )}
                            </span>
                          </th>
                        ))}
                        <th scope="col" className="!px-3 !py-4 text-left text-sm font-medium" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border bg-background">
                      {mappedNumbers &&
                        mappedNumbers.length > 0 &&
                        returnArray(mappedNumbers).map((item, index) => (
                          <tr
                            key={item.id}
                            className={`hover:bg-gray-50 cursor-pointer ${index % 2 !== 0 ? "" : ""}`}
                            onClick={() => handleRowClick(item)}
                          >
                            {returnArray(sortedVisibleColumns).map(({ key: colKey }, colIndex) => (
                              <td
                                key={colKey}
                                className={`whitespace-nowrap px-4 text-sm text-gray-900 dark:text-[#F7F7F7] ${
                                  colIndex === 0 && item.spam_status === "flagged"
                                    ? "border-l-[3px] border-l-red-500"
                                    : colIndex === 0 && item.spam_status === "replaced"
                                    ? "border-l-[3px] border-l-[#717680]"
                                    : ""
                                }`}
                              >
                                {renderCellContent(colKey, item)}
                              </td>
                            ))}
                            <td className="whitespace-nowrap !px-3 !py-4">
                              <div className="flex items-center gap-4 justify-end">
                                {item.spam_status === "flagged" && (
                                  <Button
                                    size="sm"
                                    className="bg-[#7F56D9] hover:bg-purple-700 text-white font-semibold h-8 py-2 px-3 text-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setReplaceModalNumber(item);
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1">
                                      <path d="M9.99992 17.5007L7.49992 15.0007M7.49992 15.0007L9.99992 12.5007M7.49992 15.0007H11.6666C14.8882 15.0007 17.4999 12.389 17.4999 9.16732C17.4999 6.84032 16.1374 4.83156 14.1666 3.89534M4.16659 14.4393C2.1958 13.5031 0.833252 11.4943 0.833252 9.16732C0.833252 5.94566 3.44492 3.33398 6.66659 3.33398H10.8333M10.8333 3.33398L8.33325 0.833984M10.8333 3.33398L8.33325 5.83398" stroke="#D6BBFB" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Rotate
                                  </Button>
                                )}
                                <Pen className="h-4 w-4 text-gray-400 hover:text-gray-600 mr-2" />
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {(isLoading || isInitialLoad) && <TableLoader />}

        {isError && (
          <NoData title="Error loading phone numbers" description="Please try again or contact support" />
        )}

        {shouldShowFilteredEmptyState && (
          <NoData
          className={"flex justify-center items-center flex-col"}
            heading="No phone numbers match your filters"
            description="Try adjusting or clearing filters to see more results."
          />
        )}

        {shouldShowEmptyState && <PhoneInventoryEmptyState />}

        {/* Pagination */}
        <div className="flex items-center justify-between rounded-[--radius] py-3">
          <div className="flex-1">
            {paginationInfo && paginationInfo.total > 0 && <Pagination data={paginationInfo} getPageNumber={getPageNumber} />}
          </div>
        </div>
      </div>

      {/* Purchase Number Panel */}
      <PurchaseNumberPanel />

      {/* Replace Number Modal */}
      {replaceModalNumber && (
        <ReplaceNumberModal
          isOpen={!!replaceModalNumber}
          currentNumber={replaceModalNumber}
          onClose={() => setReplaceModalNumber(null)}
          workspaceId={activeWorkspace?.id}
          onSuccess={() => queryClient.invalidateQueries(queryKeyName)}
        />
      )}
    </div>
  );
}
